
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
    Search,
    User,
    Phone,
    FileText,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Briefcase,
    MapPin,
    Calendar,
    X,
    Download
} from 'lucide-react';

const EligibilityInquiriesPage = () => {
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [pagination, setPagination] = useState({});

    const fetchInquiries = async (pageNum = 1, search = searchTerm, start = startDate, end = endDate) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let url = `/api/admin/eligibility-inquiries?page=${pageNum}&limit=10&search=${search}`;

            if (start) url += `&startDate=${start}`;
            if (end) url += `&endDate=${end}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setInquiries(response.data.data);
                setPagination(response.data.pagination);
                setPage(pageNum);
            }
        } catch (error) {
            console.error('Error fetching inquiries:', error);
            toast.error('Failed to load inquiries');
        } finally {
            setLoading(false);
        }
    };

    const handleDateSearch = () => {
        fetchInquiries(1, searchTerm, startDate, endDate);
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
        fetchInquiries(1, searchTerm, '', '');
    };

    const handleExportToExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `/api/admin/eligibility-inquiries/export?search=${searchTerm}`;

            if (startDate) url += `&startDate=${startDate}`;
            if (endDate) url += `&endDate=${endDate}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `eligibility_inquiries_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success('Excel file downloaded successfully!');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export data');
        }
    };

    // Check if download button should be shown
    const showDownloadButton = searchTerm || startDate || endDate;

    // State for modal
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleViewResults = (inquiry) => {
        setSelectedInquiry(inquiry);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedInquiry(null);
    };

    // Helper: Parse scorecard answers
    const parseAnswers = (answersData) => {
        if (!answersData) return [];
        try {
            const parsed = typeof answersData === 'string' ? JSON.parse(answersData) : answersData;
            // Allow both array or object format
            if (Array.isArray(parsed)) return parsed;
            if (typeof parsed === 'object') {
                return Object.entries(parsed).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
            }
            return [];
        } catch (e) {
            console.error('Error parsing answers:', e);
            return [];
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchInquiries(1, searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* ... (Header and Filters remain same) ... */}

            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">Eligibility Enquiries</h1>
                        <p className="text-slate-500 mt-1">Manage and review loan eligibility checks (v2)</p>
                    </div>

                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                        {/* Global Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search name, mobile, purpose..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                            />
                        </div>

                        {/* Download Button - Only visible when filters are active */}
                        {showDownloadButton && (
                            <button
                                onClick={handleExportToExcel}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                            >
                                <Download size={18} />
                                Download Excel
                            </button>
                        )}
                    </div>
                </div>

                {/* Date Range Filter */}
                <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                        <div className="flex items-center gap-2">
                            <Calendar className="text-slate-400" size={18} />
                            <span className="text-sm font-medium text-slate-600">Date Range:</span>
                        </div>

                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="flex flex-col">
                                <label className="text-xs text-slate-500 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className="text-xs text-slate-500 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div className="flex gap-2 mt-5">
                                <button
                                    onClick={handleDateSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                    <Search size={16} />
                                    Search
                                </button>

                                <button
                                    onClick={handleClearDates}
                                    className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                    <X size={16} />
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">S.No</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Profession</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Score</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Results</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                                        <div className="flex justify-center items-center gap-2">
                                            <Loader2 className="animate-spin h-5 w-5 text-blue-600" />
                                            <span>Loading inquiries...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : inquiries.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                                        No inquiries found.
                                    </td>
                                </tr>
                            ) : (
                                inquiries.map((inquiry, index) => (
                                    <tr key={inquiry.id} className="hover:bg-slate-50 transition-colors cursor-default group">
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {(pagination.page - 1) * 10 + index + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                                    <User size={16} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-slate-700">{inquiry.name}</span>
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                                                        <Phone size={12} />
                                                        {inquiry.mobile}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                <Briefcase size={14} className="text-slate-400" />
                                                {inquiry.profession}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {/* Score Column */}
                                            <div className="text-sm font-semibold text-slate-700">
                                                {inquiry.score !== null ? inquiry.score : 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {/* Results Column */}
                                            <button
                                                onClick={() => handleViewResults(inquiry)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                                title="View Detail Results"
                                            >
                                                <FileText size={18} />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-start gap-2">
                                                <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                                                <div className="flex flex-col text-sm text-slate-600">
                                                    <span>{inquiry.city}, {inquiry.state}</span>
                                                    <span className="text-xs text-slate-400">{inquiry.pincode}</span>
                                                    <span className="text-xs text-slate-500 mt-1 line-clamp-1" title={inquiry.purpose}>
                                                        {inquiry.purpose}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            <div className="flex items-center gap-2">
                                                <Calendar size={14} className="text-slate-400" />
                                                {new Date(inquiry.created_at).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-slate-500">
                            Showing <span className="font-medium">{(pagination.page - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(pagination.page * 10, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => fetchInquiries(page - 1, searchTerm)}
                                disabled={page === 1}
                                className="p-2 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    onClick={() => fetchInquiries(p, searchTerm)}
                                    className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${p === page
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                        }`}
                                >
                                    {p}
                                </button>
                            ))}

                            <button
                                onClick={() => fetchInquiries(page + 1, searchTerm)}
                                disabled={page === pagination.totalPages}
                                className="p-2 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Results Modal */}
            {showModal && selectedInquiry && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Assessment Results</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedInquiry.name} • {selectedInquiry.mobile}
                                </p>
                            </div>
                            <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto">
                            {/* Score Card in Modal */}
                            <div className="mb-6 bg-blue-50 border border-blue-100 rounded-lg p-5 flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-semibold text-blue-800 uppercase tracking-wide">Eligibility Score</h4>
                                    <p className="text-xs text-blue-600 mt-0.5">Calculated based on responses</p>
                                </div>
                                <div className="text-3xl font-bold text-blue-700">
                                    {selectedInquiry.score ?? 'N/A'}
                                </div>
                            </div>

                            <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                <FileText size={18} />
                                Question & Answer Record
                            </h4>

                            <div className="space-y-4">
                                {parseAnswers(selectedInquiry.scorecard_answers).length > 0 ? (
                                    parseAnswers(selectedInquiry.scorecard_answers).map((ans, idx) => (
                                        <div key={idx} className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mt-0.5">
                                                    {idx + 1}
                                                </div>
                                                <div className="flex-grow">
                                                    <p className="text-sm font-medium text-gray-800 mb-1">
                                                        Question {idx + 1}
                                                    </p>
                                                    <p className="text-sm text-gray-600">
                                                        Answer: <span className="font-semibold text-gray-900">{typeof ans.value === 'object' ? JSON.stringify(ans.value) : (ans.value || ans)}</span>
                                                    </p>
                                                    {ans.points && (
                                                        <span className="inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded bg-green-100 text-green-700">
                                                            + {ans.points} Points
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500 italic">
                                        No question details available for this inquiry.
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EligibilityInquiriesPage;
